import React, { useEffect, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import CommonModal from "./CommonModal";
import { callAPI } from "../../utils/apiUtils";
import { apiUrls } from "../../utils/apiUrls";

/**
 * Row-level detail behind one cell of the Revenue page's "Deleted Items & Orders by Staff"
 * table - which item (or order) was deleted, off which order, and when. `employeeId` is left
 * empty for the "Unknown staff" bucket (deletions with no attributed employee).
 */
const DeletedDetailModal = ({ isOpen, onClose, type, employeeId, employeeName, startDate, endDate }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchDetail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, type, employeeId, startDate, endDate]);

  const fetchDetail = async () => {
    setLoading(true);
    try {
      const url = type === "items" ? apiUrls.REVENUE_DELETED_ITEMS_DETAIL : apiUrls.REVENUE_DELETED_ORDERS_DETAIL;
      const employeeParam = employeeId != null ? `&employee_id=${employeeId}` : "";
      const res = await callAPI(`${url}?start_date=${startDate}&end_date=${endDate}${employeeParam}`);
      setRows(res.data?.data?.rows || []);
    } catch (err) {
      console.error("Failed to load deletion detail:", err);
      toast.error("Failed to load details");
    } finally {
      setLoading(false);
    }
  };

  const title = type === "items" ? "Items Deleted" : "Orders Deleted";

  return (
    <CommonModal isOpen={isOpen} onClose={onClose} elId="deletedDetailModal">
      <div className="modal-header">
        <h5 className="modal-title">
          {title} — {employeeName || "Unknown staff"}
        </h5>
        <button className="btn-close" onClick={onClose}></button>
      </div>
      <div className="modal-body">
        <div className="table-responsive">
          <table className="table table-striped table-bordered text-nowrap">
            {type === "items" ? (
              <>
                <thead>
                  <tr>
                    <th>Order #</th>
                    <th>Item</th>
                    <th>Deleted At</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="3" className="text-center">Loading...</td></tr>
                  ) : rows.length === 0 ? (
                    <tr><td colSpan="3" className="text-center">No deletions found</td></tr>
                  ) : (
                    rows.map((row) => (
                      <tr key={row.id}>
                        <td>{row.order_number || row.order_id}</td>
                        <td>{row.item_name || "Unknown item"}</td>
                        <td>{moment.unix(row.created_at).format("DD MMM YYYY, hh:mm A")}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </>
            ) : (
              <>
                <thead>
                  <tr>
                    <th>Order #</th>
                    <th>Table</th>
                    <th>Amount</th>
                    <th>Deleted At</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="4" className="text-center">Loading...</td></tr>
                  ) : rows.length === 0 ? (
                    <tr><td colSpan="4" className="text-center">No deletions found</td></tr>
                  ) : (
                    rows.map((row) => (
                      <tr key={row.id}>
                        <td>{row.orderid || row.id}</td>
                        <td>{row.table_id || "-"}</td>
                        <td>${row.FinalAmount}</td>
                        <td>{moment(row.deleted_at).format("DD MMM YYYY, hh:mm A")}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </>
            )}
          </table>
        </div>
      </div>
      <div className="modal-footer">
        <button className="btn btn-secondary" onClick={onClose}>Close</button>
      </div>
    </CommonModal>
  );
};

export default DeletedDetailModal;
